import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Calendar, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quote");
      setQuotes(data);
    } catch (e) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await api.delete(`/quote/${id}`);
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-6xl">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Inbox</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Quote Requests</h1>
        <p className="mt-2 text-white/60 text-sm">Every submission from the site contact form.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-white/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : quotes.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-10 text-center text-white/55">
          No quote requests yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="quote-list">
          {quotes.map((q) => (
            <div key={q.id} className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg font-bold">{q.full_name}</h3>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/25 font-bold">
                    {q.service}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/65">
                  <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#D4AF37]" /> {q.email}</span>
                  <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#D4AF37]" /> {q.phone}</span>
                  <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#D4AF37]" /> {new Date(q.created_at).toLocaleString()}</span>
                </div>
                {q.message && (
                  <p className="mt-3 text-sm text-white/75 whitespace-pre-wrap">{q.message}</p>
                )}
              </div>
              <button
                onClick={() => del(q.id)}
                data-testid={`quote-delete-${q.id}`}
                className="self-start shrink-0 inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-red-400 px-3 py-2 rounded-lg hover:bg-red-500/10 transition-all"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
